import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { policyholderAuctioneers } from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { SkeletonText } from '@/shared/ui/Skeleton';

import { fieldErrorOf } from '../shared/fieldError';

import { MAX_MATRICULA, useUfs } from './leiloeiro';

/** Os campos da UF da junta e da matricula, comuns ao cadastro e a ficha. */
export function CamposDoLeiloeiro({
  uf,
  matricula,
  onUf,
  onMatricula,
  apiError,
  obrigatorio,
}: {
  uf: string;
  matricula: string;
  onUf: (valor: string) => void;
  onMatricula: (valor: string) => void;
  apiError: ApiError | null;
  obrigatorio: boolean;
}) {
  const ufs = useUfs();
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Select
        label="UF da junta comercial"
        required={obrigatorio}
        placeholder="Selecione a UF"
        value={uf}
        options={(ufs.data ?? []).map((estado) => ({
          value: estado.code,
          label: `${estado.code} - ${estado.name}`,
        }))}
        error={fieldErrorOf(apiError, 'registry_state')}
        onChange={(event) => onUf(event.target.value)}
      />
      <Field
        label="Matricula na junta comercial"
        value={matricula}
        maxLength={MAX_MATRICULA}
        placeholder="Digite aqui"
        hint="Opcional."
        error={fieldErrorOf(apiError, 'registration_number')}
        onChange={(event) => onMatricula(event.target.value)}
      />
    </div>
  );
}

/**
 * Dados do leiloeiro do tomador pessoa fisica (P-NCT-3): o "tipo" leiloeiro, a UF da
 * junta comercial em que ele e matriculado e o numero da matricula. No OnPoint e o
 * cartao "Dados do Leiloeiro" (campos nao lidos); a matricula entra como dado, sem
 * regra. Nada aqui decide aceitacao, credito ou valor da garantia. Ver
 * docs/ADR/0066-cotacao-pessoa-fisica-e-leiloeiro.md.
 */
export function DadosDoLeiloeiro({
  policyholderId,
  podeEditar,
}: {
  policyholderId: string;
  podeEditar: boolean;
}) {
  const queryClient = useQueryClient();
  const chave = ['policyholder-auctioneer', policyholderId];
  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) => policyholderAuctioneers.get(policyholderId, signal),
    enabled: policyholderId !== '',
  });
  const [editando, setEditando] = useState(false);
  const [uf, setUf] = useState('');
  const [matricula, setMatricula] = useState('');

  const gravar = useMutation({
    mutationFn: () =>
      policyholderAuctioneers.set(policyholderId, {
        registry_state: uf,
        registration_number: matricula,
      }),
    onSuccess: (dados) => {
      queryClient.setQueryData(chave, dados);
      setEditando(false);
    },
  });
  const tirar = useMutation({
    mutationFn: () => policyholderAuctioneers.remove(policyholderId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: chave }),
  });
  const apiError = gravar.error instanceof ApiError ? gravar.error : null;

  if (consulta.isPending) return <SkeletonText lines={2} />;
  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel ler os dados do leiloeiro">
        {consulta.error.message}
      </Alert>
    );
  }
  const dados = consulta.data;
  if (!dados.is_individual) {
    return (
      <Alert tone="info" title="Dados do leiloeiro">
        Os dados de leiloeiro sao do tomador pessoa fisica (CPF).
      </Alert>
    );
  }

  if (editando) {
    return (
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          gravar.mutate();
        }}
      >
        {gravar.isError ? (
          <Alert tone="danger" title="Nao foi possivel gravar">
            {gravar.error.message}
          </Alert>
        ) : null}
        <CamposDoLeiloeiro
          uf={uf}
          matricula={matricula}
          onUf={setUf}
          onMatricula={setMatricula}
          apiError={apiError}
          obrigatorio
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setEditando(false)}>
            Cancelar
          </Button>
          <Button type="submit" loading={gravar.isPending} disabled={uf === ''}>
            Salvar dados do leiloeiro
          </Button>
        </div>
      </form>
    );
  }

  const leiloeiro = dados.auctioneer;
  return (
    <div className="flex flex-col gap-3">
      {tirar.isError ? (
        <Alert tone="danger" title="Nao foi possivel tirar a marca">
          {tirar.error.message}
        </Alert>
      ) : null}
      {dados.is_auctioneer && leiloeiro ? (
        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-2xs font-semibold text-ink-500">Tipo</dt>
            <dd className="text-ink-900">Leiloeiro</dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold text-ink-500">
              Junta comercial (UF)
            </dt>
            <dd className="text-ink-900">{leiloeiro.registry_state}</dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold text-ink-500">Matricula</dt>
            <dd className="text-ink-900">
              {leiloeiro.registration_number || 'Nao informada'}
            </dd>
          </div>
          <p className="text-2xs text-ink-500 sm:col-span-3">
            Atualizado por {leiloeiro.updated_label || 'sistema'} em{' '}
            {formatDateTime(leiloeiro.updated_at)}.
          </p>
        </dl>
      ) : (
        <p className="text-sm text-ink-600">
          O tomador nao esta marcado como leiloeiro.
        </p>
      )}
      {podeEditar ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setUf(leiloeiro?.registry_state ?? '');
              setMatricula(leiloeiro?.registration_number ?? '');
              setEditando(true);
            }}
          >
            {dados.is_auctioneer
              ? 'Editar dados do leiloeiro'
              : 'Marcar como leiloeiro'}
          </Button>
          {dados.is_auctioneer ? (
            <Button
              size="sm"
              variant="danger"
              loading={tirar.isPending}
              onClick={() => tirar.mutate()}
            >
              Tirar a marca de leiloeiro
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
